import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, Legend, PieChart, Pie, Cell
} from 'recharts';
import { mockData } from '@/lib/mockData';
import { useTranslation } from 'react-i18next';
import { 
  Loader2, 
  BarChart3, 
  Sparkles, 
  TrendingUp, 
  Calendar, 
  Users, 
  Star,
  DollarSign,
  Activity,
  Target,
  Brain,
  Filter
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { AnalyticsFilters, StrategicMetrics, FilterState } from './components';

export function AnalyticsDashboard() {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(mockData.analytics);
  const [filters, setFilters] = useState<FilterState | null>(null);
  const [businessId, setBusinessId] = useState<string>(''); 
  
  const { t } = useTranslation('business', { keyPrefix: 'analytics' });
  const { t: tc } = useTranslation('common');

  // Simulate API call
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        setData(mockData.analytics);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleFiltersChange = (newFilters: FilterState) => {
    setFilters(newFilters);
    // In a real app, this would trigger data refetch with new filters
    console.log('Filters updated:', newFilters);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
        <div className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white rounded-lg p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-purple-200 border border-purple-200/50 flex items-center justify-center mx-auto mb-4 shadow-sm">
            <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
          </div>
          <h3 className="font-semibold text-slate-900 mb-2">{t('loadingStrategicIntelligence')}</h3>
          <p className="text-slate-600 text-sm">{t('analyzingBusinessData')}</p>
        </div>
      </div>
    );
  }

  // Enhanced mock data for tourism analytics
  const tourismData = {
    revenueData: [
      { month: 'Jan', revenue: 35000, bookings: 45, avgBookingValue: 778 },
      { month: 'Feb', revenue: 42000, bookings: 52, avgBookingValue: 808 },
      { month: 'Mar', revenue: 38000, bookings: 48, avgBookingValue: 792 },
      { month: 'Apr', revenue: 45000, bookings: 58, avgBookingValue: 776 },
      { month: 'May', revenue: 52000, bookings: 65, avgBookingValue: 800 },
      { month: 'Jun', revenue: 58000, bookings: 72, avgBookingValue: 806 }
    ],
    activityData: [
      { name: 'Whale Watching', value: 145, percentage: 32, revenue: 43500, avgRating: 4.8, capacity: 180 },
      { name: 'Safari Tours', value: 122, percentage: 27, revenue: 36600, avgRating: 4.6, capacity: 150 },
      { name: 'Bird Watching', value: 89, percentage: 20, revenue: 17800, avgRating: 4.4, capacity: 120 },
      { name: 'Marine Adventure', value: 67, percentage: 15, revenue: 20100, avgRating: 4.7, capacity: 80 },
      { name: 'Photography Tours', value: 28, percentage: 6, revenue: 8400, avgRating: 4.9, capacity: 40 },
      { name: 'Hiking Trails', value: 45, percentage: 10, revenue: 6750, avgRating: 4.3, capacity: 60 },
      { name: 'Kayaking', value: 38, percentage: 8, revenue: 7600, avgRating: 4.5, capacity: 50 },
      { name: 'Sunset Cruise', value: 52, percentage: 12, revenue: 15600, avgRating: 4.6, capacity: 70 },
      { name: 'Wildlife Photography', value: 23, percentage: 5, revenue: 6900, avgRating: 4.8, capacity: 30 },
      { name: 'Conservation Tour', value: 19, percentage: 4, revenue: 3800, avgRating: 4.7, capacity: 25 }
    ],
    customerData: [
      { segment: 'New Customers', value: 65, color: '#10b981' },
      { segment: 'Returning Customers', value: 35, color: '#3b82f6' }
    ],
    seasonalData: [
      { month: 'Jan', occupancy: 65, weatherScore: 7.2 },
      { month: 'Feb', occupancy: 72, weatherScore: 7.8 },
      { month: 'Mar', occupancy: 68, weatherScore: 8.1 },
      { month: 'Apr', occupancy: 85, weatherScore: 8.5 },
      { month: 'May', occupancy: 92, weatherScore: 9.1 },
      { month: 'Jun', occupancy: 88, weatherScore: 8.8 }
    ]
  };

  return (
    <div className="space-y-8">
      {/* Strategic Intelligence Header */}
      <div className="relative">
        <div className="flex items-start justify-between">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/25">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 flex items-center justify-center shadow-md">
                  <Sparkles className="w-2.5 h-2.5 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 bg-clip-text text-transparent">
                  {t('strategicIntelligenceCenter')}
                </h1>
                <p className="text-slate-600 mt-1 leading-relaxed">
                  {t('deepAnalytics')}
                </p>
              </div>
            </div>
            
            {/* Strategic Status Pills */}
            <div className="flex items-center gap-4 pt-2">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200/80 shadow-sm">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-700">Revenue +19.6%</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200/80 shadow-sm">
                <Activity className="w-3.5 h-3.5 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">
                  {filters ? `Filtered Data` : 'All Data'}
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-200/80 shadow-sm">
                <Target className="w-3.5 h-3.5 text-purple-600" />
                <span className="text-sm font-medium text-purple-700">8 KPIs Tracked</span>
              </div>
            </div>
          </div>

          {/* Export and View Actions */}
          <div className="flex items-center gap-3">
            <Button variant="outline" className="border-slate-200/80 shadow-sm hover:shadow-md transition-shadow">
              <Filter className="w-4 h-4 mr-2" />
              {t('exportReport')}
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700 shadow-md hover:shadow-lg transition-shadow"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              {t('customDashboard')}
            </Button>
          </div>
        </div>
      </div>

      {/* Interactive Filters */}
      <AnalyticsFilters onFiltersChange={handleFiltersChange} />

      {/* Strategic Metrics */}
      <StrategicMetrics />

      {/* Advanced Tourism Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Revenue Trends & Forecasting */}
        <div className="lg:col-span-2">
          <Card className="border border-slate-200/60 shadow-lg shadow-slate-900/8 bg-white hover:shadow-xl hover:shadow-slate-900/12 transition-shadow duration-300 h-full">
            <CardHeader className="bg-slate-50/80 border-b border-slate-200/50 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-100 to-emerald-200 border border-emerald-200/50 flex items-center justify-center shadow-sm">
                  <TrendingUp className="w-5 h-5 text-emerald-700" />
                </div>
                <div>
                  <CardTitle className="text-lg font-semibold text-slate-900">Revenue & Performance Trends</CardTitle>
                  <p className="text-sm text-slate-600 mt-1">Multi-metric analysis with booking value correlation</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={tourismData.revenueData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fill: '#64748b', fontSize: 12 }} 
                      tickLine={{ stroke: '#cbd5e1' }}
                    />
                    <YAxis 
                      yAxisId="left"
                      tick={{ fill: '#64748b', fontSize: 12 }} 
                      tickLine={{ stroke: '#cbd5e1' }}
                    />
                    <YAxis 
                      yAxisId="right"
                      orientation="right"
                      tick={{ fill: '#64748b', fontSize: 12 }} 
                      tickLine={{ stroke: '#cbd5e1' }}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                      formatter={(value, name) => {
                        if (name === 'revenue') return [`$${value.toLocaleString()}`, 'Revenue'];
                        if (name === 'avgBookingValue') return [`$${value}`, 'Avg Booking Value'];
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Line 
                      yAxisId="left"
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#10b981" 
                      strokeWidth={3}
                      name="Revenue ($)" 
                      dot={{ fill: '#10b981', strokeWidth: 2, r: 5 }}
                    />
                    <Line 
                      yAxisId="right"
                      type="monotone" 
                      dataKey="bookings" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      name="Bookings" 
                      dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                    />
                    <Line 
                      yAxisId="right"
                      type="monotone" 
                      dataKey="avgBookingValue" 
                      stroke="#8b5cf6" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      name="Avg Booking Value ($)" 
                      dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Customer Segmentation */}
        <Card className="border border-slate-200/60 shadow-lg shadow-slate-900/8 bg-white hover:shadow-xl hover:shadow-slate-900/12 transition-shadow duration-300">
          <CardHeader className="bg-slate-50/80 border-b border-slate-200/50 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 border border-blue-200/50 flex items-center justify-center shadow-sm">
                <Users className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold text-slate-900">Customer Segments</CardTitle>
                <p className="text-sm text-slate-600 mt-1">New vs Returning Analysis</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="h-[250px] mb-6">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={tourismData.customerData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {tourismData.customerData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [`${value}%`, 'Percentage']}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3">
              {tourismData.customerData.map((segment, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: segment.color }}
                    />
                    <span className="text-sm text-slate-700">{segment.segment}</span>
                  </div>
                  <span className="font-semibold text-slate-900">{segment.value}%</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

      </div>

      {/* Activity Performance & Seasonal Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Wildy Redirect Clicks Over Time */}
        <Card className="border border-slate-200/60 shadow-lg shadow-slate-900/8 bg-white hover:shadow-xl hover:shadow-slate-900/12 transition-shadow duration-300">
          <CardHeader className="bg-slate-50/80 border-b border-slate-200/50 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-100 to-emerald-200 border border-emerald-200/50 flex items-center justify-center shadow-sm">
                <BarChart3 className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold text-slate-900">Wildy Redirect Clicks</CardTitle>
                <p className="text-sm text-slate-600 mt-1">Number of clicks to Wildy website (non-paying users only)</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={[
                  { date: '2024-06-01', clicks: 12 },
                  { date: '2024-06-02', clicks: 18 },
                  { date: '2024-06-03', clicks: 9 },
                  { date: '2024-06-04', clicks: 15 },
                  { date: '2024-06-05', clicks: 22 },
                  { date: '2024-06-06', clicks: 17 },
                  { date: '2024-06-07', clicks: 25 },
                  { date: '2024-06-08', clicks: 19 },
                  { date: '2024-06-09', clicks: 14 },
                  { date: '2024-06-10', clicks: 20 }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 12 }} tickLine={{ stroke: '#cbd5e1' }} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickLine={{ stroke: '#cbd5e1' }} allowDecimals={false} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                    formatter={(value) => [`${value} clicks`, 'Redirects']}
                  />
                  <Line type="monotone" dataKey="clicks" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', strokeWidth: 2, r: 5 }} name="Clicks" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 text-xs text-slate-500">
              <span className="font-semibold text-emerald-700">Note:</span> This metric is only available for users who do not pay for the booking system with Wildy.
            </div>
          </CardContent>
        </Card>

        {/* Seasonal & Operational Analysis */}
        <Card className="border border-slate-200/60 shadow-lg shadow-slate-900/8 bg-white hover:shadow-xl hover:shadow-slate-900/12 transition-shadow duration-300">
          <CardHeader className="bg-slate-50/80 border-b border-slate-200/50 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-100 to-amber-200 border border-amber-200/50 flex items-center justify-center shadow-sm">
                <Activity className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold text-slate-900">Seasonal Performance</CardTitle>
                <p className="text-sm text-slate-600 mt-1">Occupancy rates vs weather impact</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={tourismData.seasonalData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fill: '#64748b', fontSize: 12 }} 
                    tickLine={{ stroke: '#cbd5e1' }}
                  />
                  <YAxis 
                    yAxisId="left"
                    tick={{ fill: '#64748b', fontSize: 12 }} 
                    tickLine={{ stroke: '#cbd5e1' }}
                    domain={[0, 100]}
                  />
                  <YAxis 
                    yAxisId="right"
                    orientation="right"
                    tick={{ fill: '#64748b', fontSize: 12 }} 
                    tickLine={{ stroke: '#cbd5e1' }}
                    domain={[0, 10]}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                    formatter={(value, name) => {
                      if (name === 'occupancy') return [`${value}%`, 'Occupancy Rate'];
                      if (name === 'weatherScore') return [`${value}/10`, 'Weather Score'];
                      return [value, name];
                    }}
                  />
                  <Legend />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="occupancy" 
                    stroke="#f59e0b" 
                    strokeWidth={3}
                    name="Occupancy Rate (%)" 
                    dot={{ fill: '#f59e0b', strokeWidth: 2, r: 5 }}
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="weatherScore" 
                    stroke="#06b6d4" 
                    strokeWidth={2}
                    strokeDasharray="8 8"
                    name="Weather Score (1-10)" 
                    dot={{ fill: '#06b6d4', strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

      </div>

      {/* Strategic Insights & Recommendations */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white">
          <CardContent className="p-6">
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 border border-blue-200/50 flex items-center justify-center shadow-sm">
                  <Brain className="w-4 h-4 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">Strategic Recommendations</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-4 border border-emerald-200/60 rounded-lg bg-emerald-50/30">
                  <div className="flex items-start gap-3">
                    <TrendingUp className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-emerald-900">Revenue Optimization</h4>
                      <p className="text-sm text-emerald-700 mt-1">
                        Focus on high-value whale watching tours during peak season. Avg booking value trending up 17.8%.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 border border-blue-200/60 rounded-lg bg-blue-50/30">
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-900">Customer Retention</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        34.8% repeat customers indicates strong satisfaction. Implement loyalty program for further growth.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-4 border border-purple-200/60 rounded-lg bg-purple-50/30">
                  <div className="flex items-start gap-3">
                    <Activity className="w-5 h-5 text-purple-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-purple-900">Operational Efficiency</h4>
                      <p className="text-sm text-purple-700 mt-1">
                        78.5% guide utilization is good. Consider expanding capacity during May-June peak period.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

    </div>
  );
}